import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { Settings } from 'lucide-react';
import { getVesselAuxEnginePowerData } from '../../../../services/vesselMachineryService';
import { generateDateTicks } from '../../../../services/dataTicks';

const AEPowerGeneratorChart = () => {
  const selectedAE = useSelector(state => state.ae.selectedAE);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { selectedVoyage } = useSelector((state) => state.voyageFilter);

  const [powerData, setPowerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch power data
  const fetchPowerData = async () => {
    if (!currentVessel?._id || !selectedAE) {
      setPowerData(null);
      return;
    }

    if (!selectedVoyage && (!startDate || !endDate)) {
      setPowerData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getVesselAuxEnginePowerData(
        currentVessel._id,
        selectedAE,
        selectedVoyage ? null : startDate,
        selectedVoyage ? null : endDate,
        selectedVoyage?._id || null
      );

      console.log("AE Power Data Response:", response);

      if (response?.status === "success") {
        setPowerData(response.data);
      } else {
        setError(response?.message || "Failed to fetch power data");
        // Set empty data structure on error
        setPowerData({
          timeSeriesData: [],
          statistics: { averagePower: 0, maxPower: 0, totalPowerOutput: 0 },
          dateRange: { days: 0 }
        });
      }
    } catch (err) {
      console.error("Error fetching AE power data:", err);
      setError(err.message);
      // Set empty data structure on error
      setPowerData({
        timeSeriesData: [],
        statistics: { averagePower: 0, maxPower: 0, totalPowerOutput: 0 },
        dateRange: { days: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPowerData();
  }, [currentVessel, selectedAE, startDate, endDate, selectedVoyage]);

  // Get engine color based on selected AE
  const getEngineColor = (engineId) => {
    const colorMap = {
      'ae1': '#1a5276',
      'ae2': '#d35400',
      'ae3': '#27ae60',
      'ae4': '#7d3c98',
      'ae5': '#a93226',
      'ae6': '#b7950b'
    };
    return colorMap[engineId] || '#1a5276';
  };

  // Transform API data to Highcharts format - ALWAYS returns data
  const transformDataForChart = () => {
    if (!powerData?.timeSeriesData || powerData.timeSeriesData.length === 0) {
      return {
        categories: [],
        series: [{
          name: `${selectedAE?.toUpperCase()} Power Output`,
          data: [],
          color: getEngineColor(selectedAE),
          type: 'area'
        }],
        dates: []
      };
    }

    const categories = powerData.timeSeriesData.map(item => item.date);
    const dates = powerData.timeSeriesData.map(item => item.fullDate);

    const series = [{
      name: `${selectedAE?.toUpperCase()} Power Output`,
      data: powerData.timeSeriesData.map(item => ({
        x: new Date(item.fullDate).getTime(),
        y: item.powerOutput,
        recordCount: item.recordCount,
        hasData: item.hasData
      })),
      color: getEngineColor(selectedAE),
      type: 'area'
    }];

    return {
      categories,
      series,
      dates
    };
  };

  const chartData = transformDataForChart();

  const tickPositions = chartData.dates && chartData.dates.length > 0
    ? generateDateTicks(chartData.dates)
    : [];

  const options = {
    chart: {
      type: 'area',
      height: '300',
      backgroundColor: 'transparent',
      style: {
        fontFamily: "'Inter', 'Segoe UI', sans-serif"
      }
    },
    title: { text: null },
    credits: { enabled: false },
    xAxis: {
      type: 'datetime',
      tickPositions: tickPositions,
      title: {
        text: 'Days',
        style: {
          color: '#718096',
          fontSize: '12px',
          fontWeight: '500'
        }
      },
      labels: {
        style: {
          color: '#4a5568',
          fontSize: '11px'
        },
        rotation: 0,
        format: '{value:%b %e}',
      },
      gridLineWidth: 1,
      gridLineColor: '#f0f0f0',
    },
    yAxis: {
      title: {
        text: 'Power (kW)',
        style: {
          color: '#718096',
          fontSize: '12px',
          fontWeight: '500'
        }
      },
      min: 0,
      startOnTick: true,
      minPadding: 0,
      maxPadding: 0.05,
      tickAmount: 6,
      labels: {
        style: {
          color: '#718096'
        }
      }
    },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: {
        color: '#566573',
        fontSize: '11px',
        fontWeight: '500'
      }
    },
    plotOptions: {
      area: {
        stacking: 'normal',
        lineWidth: 2,
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 3,
          lineWidth: 1,
        },
        states: {
          hover: {
            enabled: true,
            lineWidth: 3
          }
        }
      }
    },
    series: chartData.series,
    tooltip: {
      shared: true,
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: '#e2e8f0',
      borderRadius: 8,
      shadow: true,
      style: {
        fontSize: '12px'
      },
      headerFormat: '<span style="font-size: 13px; font-weight: 600; color: #1a365d">{point.key}</span><br/>',
      pointFormatter: function () {
        const dataStatus = this.point.hasData ? 'Actual data' : 'Zero value (no data)';
        return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${this.y.toFixed(1)} kW</b><br/>
                <span style="font-size: 11px; color: #718096;">${dataStatus}</span>`;
      }
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500
        },
        chartOptions: {
          legend: {
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom'
          }
        }
      }]
    }
  };

  const handleRefresh = () => {
    fetchPowerData();
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex-1">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a5276]"></div>
            <p className="mt-3 text-gray-600 font-medium">Loading power data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex-1">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-[#e1f5fb] text-[#27ade3] rounded-md">
            <Settings className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800 tracking-tight">
            Power
          </h2>
        </div>
      </div>

      {/* Chart - ALWAYS show chart, even with zero data */}
      <div className='p-6'>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
        />
      </div>
    </div>
  );
};

export default AEPowerGeneratorChart;